/**
 *
 * @module accountApi
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/account.md
 * @see https://github.bamtech.co/sdk-distribution/bam-sdk/blob/master/Features/AccountApi.md
 *
 */

import Check from 'check-types';
import Logging from './../logging/entry';
import Services from './../services/entry';
import Subscription from './../subscription/entry';
import Token from './../token/entry';
import AccountManager from './accountManager';

const Util = Services.Util;
const Account = Services.Account.Account;
const SubscriptionManager = Subscription.SubscriptionManager;
const TokenManager = Token.TokenManager;
const AccessContextState = Token.AccessContextState;
const DustLogUtility = Services.Internal.Dust.DustLogUtility;
const AccountApiDustUrnReference = Services.Internal.Dust.DustUrnReference.account.accountApi;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    instanceStrict,
    nonEmptyString,
    object
} = Check.assert;

/**
 *
 * @class AccountApi
 * @desc Provides ability to access account data and link subscriptions to the account.
 *
 */
export default class AccountApi {

    /**
     *
     * @param {AccountManager} accountManager
     * @param {TokenManager} tokenManager
     * @param {SDK.Subscription.SubscriptionManager} subscriptionManager
     * @param {SDK.Logging.Logger} [logger=Logging]
     *
     */
    constructor(accountManager, tokenManager, subscriptionManager, logger = Logging) {

        const errorMessage = {
            ctor: 'AccountApi(accountManager, subscriptionManager, logger)',
            get accountManager() {
                return `${this.ctor} needs SDK.Account.AccountManager instance`;
            },
            get tokenManager() {
                return `${this.ctor} needs SDK.Token.TokenManager instance`;
            },
            get subscriptionManager() {
                return `${this.ctor} needs SDK.Subscription.SubscriptionManager instance`;
            }
        };

        instanceStrict(accountManager, AccountManager, errorMessage.accountManager, InvalidArgumentException);
        instanceStrict(tokenManager, TokenManager, errorMessage.tokenManager, InvalidArgumentException);
        instanceStrict(subscriptionManager, SubscriptionManager, errorMessage.subscriptionManager, InvalidArgumentException); // eslint-disable-line

        /**
         *
         * @access private
         * @type {AccountManager}
         *
         */
        this.accountManager = accountManager;

        /**
         *
         * @access private
         * @type {SDK.Token.TokenManager}
         *
         */
        this.tokenManager = tokenManager;

        /**
         *
         * @access private
         * @type {SDK.Subscription.SubscriptionManager}
         *
         */
        this.subscriptionManager = subscriptionManager;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {Boolean}
         * @desc used to enable dust logging
         *
         */
        this.dustEnabled = Util.getSafe(() => this.accountManager.client.dustEnabled);

        this.logger.info(AccountApi.name, 'Created.');

    }

    /**
     *
     * @access public
     * @since 3.5.0
     * @param {String} token
     * @param {String} provider
     * @desc Authorizes a session with an identity that has been authenticated by an external provider.
     * @throws {AuthenticationExpiredException} Authentication has expired and needs to be updated before proceeding.
     * @note Request to exchange token returns HTTP 400 with error: invalid_grant and error_description: expired-token.
     * @throws {InvalidTokenException} External identity token was invalid.
     * @note Request to obtain an account grant or create a new account with the supplied identity
     * token returns an HTTP error with code: payload.id-token.invalid.
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {LocationAcquisitionException} Unable to get user location due to location service being disabled.
     * @note Only applicable on certain platforms.
     * @note GeoProvider throws exception while trying to acquire location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<Void>}
     *
     */
    async authorize(token, provider) {

        const errorMessage = {
            method: 'AccountApi.authorize(token, provider)',
            get token() {
                return `${this.method} token should be a String with length`;
            },
            get provider() {
                return `${this.method} provider should be a String with length`;
            }
        };

        nonEmptyString(token, errorMessage.token, InvalidArgumentException);
        nonEmptyString(provider, errorMessage.provider, InvalidArgumentException);

        const { accountManager, tokenManager, dustEnabled, logger } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: AccountApi.name,
            urn: AccountApiDustUrnReference.authorize
        });

        try {

            const accountGrant = await accountManager.createAccountGrant(token);
            const accessContextState = new AccessContextState([provider]);

            return await tokenManager.exchangeGrant(accountGrant, accessContextState);

        } catch (ex) {

            if (dustEnabled) {
                dustLogUtility.captureErrors(ex);
            }

            return Promise.reject(ex);

        } finally {

            if (dustEnabled) {
                dustLogUtility.log();
            }

        }

    }

    /**
     *
     * @access public
     * @since 3.5.0
     * @param {String} token
     * @param {Object} [attributes={}] - A set of attributes to associate with the new account. Note: certain
     * attributes may be required while others are optional.
     * @note {Object} [metadata] - Object which may hold additional information about the account
     * @desc Creates an account from an identity that has been authenticated by an external provider.
     * @throws {AuthenticationExpiredException} Authentication has expired and needs to be updated before proceeding.
     * @note Request to exchange token returns HTTP 400 with error: invalid_grant and error_description: expired-token.
     * @throws {InvalidTokenException} External identity token was invalid.
     * @note Request to obtain an account grant or create a new account with the supplied identity
     * token returns an HTTP error with code: payload.id-token.invalid.
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {LocationAcquisitionException} Unable to get user location due to location service being disabled.
     * @note Only applicable on certain platforms.
     * @note GeoProvider throws exception while trying to acquire location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<SDK.Services.Account.CreateAccountResult>}
     *
     */
    async createAccount(token, attributes = {}, metadata) {

        const errorMessage = {
            method: 'AccountApi.createAccount(token, attributes, metadata)',
            get token() {
                return `${this.method} token should be a String with length`;
            },
            get attributes() {
                return `${this.method} attributes should be an Object`;
            },
            get metadata() {
                return `${this.method} metadata should be an Object`;
            }
        };

        nonEmptyString(token, errorMessage.token, InvalidArgumentException);
        object(attributes, errorMessage.attributes, InvalidArgumentException);

        if (Check.assigned(metadata)) {
            object(metadata, errorMessage.metadata, InvalidArgumentException);
        }

        const { accountManager, dustEnabled, logger } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: AccountApi.name,
            urn: AccountApiDustUrnReference.createAccount
        });

        try {

            return await accountManager.createAccount(token, attributes, metadata);

        } catch (ex) {

            if (dustEnabled) {
                dustLogUtility.captureErrors(ex);
            }

            return Promise.reject(ex);

        } finally {

            if (dustEnabled) {
                dustLogUtility.log();
            }

        }

    }

    /**
     *
     * @access public
     * @since 3.1.0
     * @desc Gets an Account instance that can be used to get information about the account.
     * @throws {AccountNotFoundException} The account was not found
     * @note The account could not be found or the operation failed. Account service returns 401 or 404
     * with error: account.get.failed, account.not.found.
     * @throws {AuthenticationExpiredException} Authentication has expired and needs to be updated before proceeding.
     * @note Request to exchange token returns HTTP 400 with error: invalid_grant and error_description: expired-token.
     * @throws {InvalidTokenException} External identity token was invalid.
     * @note Request to obtain an account grant or create a new account with the supplied identity
     * token returns an HTTP error with code: payload.id-token.invalid.
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {LocationAcquisitionException} Unable to get user location due to location service being disabled.
     * @note Only applicable on certain platforms.
     * @note GeoProvider throws exception while trying to acquire location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<SDK.Services.Account.Account>}
     *
     * @example sdkSession.accountApi.getAccount().then(account) => {
     *     console.log(account.accountId);
     *     console.log(account.attributes);
     * });
     *
     */
    async getAccount() {

        const { accountManager, dustEnabled, logger } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: AccountApi.name,
            urn: AccountApiDustUrnReference.getAccount
        });

        try {

            return await accountManager.getAccount();

        } catch (ex) {

            if (dustEnabled) {
                dustLogUtility.captureErrors(ex);
            }

            return Promise.reject(ex);

        } finally {

            if (dustEnabled) {
                dustLogUtility.log();
            }

        }

    }

    /**
     *
     * @access public
     * @since 3.2.0
     * @param {Account} account - account to be saved
     * @desc Saves all current changes made to the account attributes.
     * @throws {AttributeValidationException} One or more attribute was invalid.
     * @note The account service returns HTTP 404 with and code of payload.fields.incorrect.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<Void>} A promise that completes when the operation has succeeded.
     *
     * @example const accountApi = sdkSession.accountApi;
     * @example accountApi.getAccount().then(account) => {
     *     account.attributes.optInUpdates = true; // update attribute
     *     account.attributes.birthDate = null; // delete attribute
     *
     *     return accountApi.updateAccount(account);
     * });
     *
     */
    async updateAccount(account) {

        const errorMessage = {
            method: 'AccountApi.updateAccount(account)',
            get account() {
                return `${this.method} needs Account instance`;
            }
        };

        instanceStrict(account, Account, errorMessage.account, InvalidArgumentException);

        const { accountManager, dustEnabled, logger } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: AccountApi.name,
            urn: AccountApiDustUrnReference.updateAccount
        });

        try {

            return await accountManager.updateAttributes(account.attributes);

        } catch (ex) {

            if (dustEnabled) {
                dustLogUtility.captureErrors(ex);
            }

            return Promise.reject(ex);

        } finally {

            if (dustEnabled) {
                dustLogUtility.log();
            }

        }

    }

    /**
     *
     * @access public
     * @desc Links (copies) all subscriptions from the user's device to the Account.
     * @throws {LinkSubscriptionPartialException} Copy partial error.
     * @note There is an error with linking the subscription to the device. Subscription service
     * returns 202 with error: copy.failure, copy.limit.reached.
     * @throws {AuthenticationExpiredException} Authentication has expired and needs to be updated before proceeding.
     * @note Request to exchange token returns HTTP 400 with error: invalid_grant and error_description: expired-token.
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {LocationAcquisitionException} Unable to get user location due to location service being disabled.
     * @note Only applicable on certain platforms.
     * @note GeoProvider throws exception while trying to acquire location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<Void>} A promise that completes when the operation has succeeded.
     *
     */
    async linkSubscriptionsFromDevice() {

        const { dustEnabled, logger, subscriptionManager } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: AccountApi.name,
            urn: AccountApiDustUrnReference.updateAccount
        });

        try {

            return await subscriptionManager.linkSubscriptionsFromDevice();

        } catch (ex) {

            if (dustEnabled) {
                dustLogUtility.captureErrors(ex);
            }

            return Promise.reject(ex);

        } finally {

            if (dustEnabled) {
                dustLogUtility.log();
            }

        }

    }

}



// WEBPACK FOOTER //
// ./src/account/accountApi.js